/**
 * Dashboard navigation config.
 *
 * Single source of truth for sidebar items + the Cmd+K palette command list.
 * Each item has a `link`, `label`, optional `icon` (MUI element), optional
 * `authorizedRoles` filter, and optional `aliases` (extra search terms for the
 * palette — e.g. "nomina" matches the "Nóminas" page).
 */

import React from 'react';
import {
  Home, PendingActions, People, WorkHistory, WorkOutline, BeachAccess, Schedule,
  Payment, Star, Analytics, MonetizationOn, AutoAwesome, MedicalInformation,
  Task, EditNote, CleaningServices, Article, AccessTime, Inventory, Business,
  Assignment, MenuBook, Sync, Science, Contacts, LocalHospital, LocalAtm,
  AccountBalance, FactCheck, NotificationsNone, ShoppingCart, CalendarMonth,
  Warehouse, Category, LocalShipping, LinkOutlined,
} from '@mui/icons-material';
import { WebAppRole } from 'hvp-shared';

export interface MenuItem {
  link: string;
  icon?: React.ReactElement;
  label: string;
  authorizedRoles?: WebAppRole[];
  /** Extra terms to match in the Cmd+K palette (synonyms, abbreviations). */
  aliases?: string[];
  /**
   * If true, the entry is excluded from the sidebar but kept in the Cmd+K
   * palette. Use for sub-routes (tabs inside a feature) that don't deserve
   * their own sidebar slot but should be reachable via the palette.
   */
  hiddenFromSidebar?: boolean;
  /** Other routes that light this entry up — e.g. «Almacenes y secciones», one entry for two screens. */
  alsoActiveOn?: string[];
}

/**
 * El fondo de la barra lateral. Vivía escrito a mano dos veces en `DashboardLayout`.
 *
 * Está aquí porque es **contra este color** contra el que hay que medir el contraste de los
 * `accent`, no contra blanco: la barra es azul pálido, así que medir sobre blanco da un número
 * optimista. Con dos copias sueltas del hex, cambiar el fondo dejaba la comprobación midiendo
 * un color que ya no existe.
 */
export const SIDEBAR_BG = '#dfeef5';

export interface MenuSection {
  title: string;
  items: MenuItem[];
  /**
   * El color del grupo. Tiñe el encabezado, su barrita y los iconos de sus entradas.
   *
   * Existe porque con nueve grupos **los encabezados se confundían entre sí**: todos eran el
   * mismo gris en mayúsculas, así que la barra se leía como una lista larga en vez de como
   * nueve bloques. *(Javier, 2026-09-12: «me gustaría distinguir entre secciones».)*
   *
   * Vive aquí y no en el componente por lo de siempre: el grupo y su color son **un dato**, y
   * separarlos es cómo se acaba con un color asignado por posición que se rompe al reordenar.
   *
   * ⚠️ Dos cuidados al elegir uno nuevo:
   * - **Contraste**: es texto pequeño en negrita, así que ≥ 4,5:1 sobre el fondo de la barra.
   *   Los nueve lo cumplen contra {@link SIDEBAR_BG}; el naranja hubo que bajarlo dos veces —
   *   `#EF6C00` daba 3,0:1 y `#D84315` se quedaba en 4,44:1—, y lo cazó la prueba, no el ojo.
   * - **No azul primario**: la entrada activa se pinta con un relleno azul sólido, y un
   *   encabezado del mismo azul se lee como si estuviera seleccionado.
   */
  accent: string;
}

/**
 * Los grupos, **por tarea y no por tecnología**. Reorganizado el 2026-09-12.
 *
 * Antes eran once, y casi todos iban por tarea salvo dos que iban por otra cosa: **✨ IA**
 * agrupaba por *cómo está construido* y **🔄 QVET** por *de dónde salen los datos*. Nadie abre
 * el menú pensando «quiero algo de QVET»: lo abre pensando «quiero ver el stock». De ahí venía
 * el desorden medido:
 *
 * - **«👥 People Management» tenía 14 de las 47 entradas visibles** —casi un tercio de la barra—
 *   mezclando tres trabajos: quién es quién, el tiempo, y la paga. Ahora son tres grupos.
 * - **«análisis» estaba en tres grupos** —Ventas en Análisis, Stock en Inventario, Rendimiento
 *   en People— porque el criterio no era el mismo en los tres.
 * - **Dos encabezados se pintaban vacíos:** «🧾 Medical», cuya única entrada está oculta, y
 *   «🔍 Análisis» para quien no fuera gerencia. Ya no existen como grupo, y además
 *   {@link visibleSidebarSections} descarta los que se quedan sin nada.
 * - Cinco títulos iban en inglés, contra la regla de UI en español del proyecto.
 *
 * **Ningún enlace cambió de sitio**, sólo de grupo: esto no toca rutas ni páginas. Lo que sí
 * cambia es dónde busca la gente, así que el coste es reaprender la barra una vez.
 *
 * ⚠️ Y la regla para mantenerlo: **un grupo no pasa de siete entradas visibles**. Cuando llega a
 * ocho, no se añade la octava — se parte el grupo, que es lo que no se hizo con People.
 */
export const menuSections: MenuSection[] = [
  {
    title: '🏠 Inicio',
    accent: '#37474F',
    items: [
      { link: '/dashboard/home', icon: <Home />, label: 'Home', aliases: ['inicio', 'principal'] },
      { link: '/dashboard/pending', icon: <PendingActions />, label: 'Pendientes' },
    ],
  },
  {
    title: '👥 Personas',
    accent: '#5E35B1',
    items: [
      { link: '/dashboard/collaborators', icon: <People />, label: 'Colaboradores', aliases: ['empleados', 'staff', 'gente'] },
      { link: '/dashboard/employments', icon: <WorkHistory />, label: 'Empleos', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/jobs', icon: <WorkOutline />, label: 'Puestos', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/salary-data', icon: <MonetizationOn />, label: 'Datos salariales', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      // Qué ficha de QVET es de cada colaborador. Va aquí y no con lo de QVET porque la pregunta
      // que contesta es «quién es quién», no «qué dice QVET».
      { link: '/dashboard/staff-links', icon: <LinkOutlined />, label: 'Enlaces de personal', aliases: ['enlaces', 'personal qvet', 'alias', 'quien es quien', 'staff links'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/staff', label: 'QVET — staff', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
    ],
  },
  {
    title: '🗓️ Jornada',
    accent: '#01579B',
    items: [
      { link: '/dashboard/attendance-records', icon: <WorkHistory />, label: 'Registro de asistencia', aliases: ['checadas', 'horarios'] },
      { link: '/dashboard/work-shifts', icon: <Schedule />, label: 'Turnos' },
      { link: '/dashboard/shift-checklist', icon: <FactCheck />, label: 'Checklist de turno', aliases: ['checklist', 'cierre de turno', 'pendientes del turno', 'actividades de turno'] },
      { link: '/dashboard/activity-register', icon: <WorkOutline />, label: 'Registro de actividades' },
      { link: '/dashboard/activity-register/new', label: 'Registro de actividades — nuevo', hiddenFromSidebar: true, aliases: ['nuevo registro'] },
      { link: '/dashboard/activity-register/calculator', label: 'Registro de actividades — calculadora', hiddenFromSidebar: true, aliases: ['calculator', 'calculadora actividades'] },
      { link: '/dashboard/activity-register/all', label: 'Registro de actividades — todos', hiddenFromSidebar: true },
      { link: '/dashboard/activity-register/types', label: 'Registro de actividades — tipos', hiddenFromSidebar: true },
      { link: '/dashboard/time-off-requests', icon: <BeachAccess />, label: 'Solicitudes de tiempo libre', aliases: ['vacaciones', 'permiso'] },
      { link: '/dashboard/calendar', icon: <CalendarMonth />, label: 'Calendario', aliases: ['calendar', 'cumpleaños', 'aniversarios', 'festivos', 'eventos'] },
      { link: '/dashboard/public-holidays', icon: <BeachAccess />, label: 'Días festivos' },
    ],
  },
  {
    title: '💵 Nómina y desempeño',
    accent: '#00695C',
    items: [
      { link: '/dashboard/payrolls', icon: <Payment />, label: 'Nóminas', aliases: ['nomina', 'salario', 'pago'] },
      { link: '/dashboard/commissions', icon: <Star />, label: 'Comisiones' },
      { link: '/dashboard/performance', icon: <Analytics />, label: 'Rendimiento', authorizedRoles: [WebAppRole.admin, WebAppRole.manager, WebAppRole.collaborator, WebAppRole.user], aliases: ['desempeño', 'mi desempeño', 'mi rendimiento', 'evaluación personal', 'salud de datos', 'sincronización', 'completitud'] },
      { link: '/dashboard/audit', icon: <FactCheck />, label: 'Auditoría', authorizedRoles: [WebAppRole.admin, WebAppRole.manager], aliases: ['clientes varios', 'duplicados', 'calidad de datos'] },
    ],
  },
  {
    title: '📦 Inventario y compras',
    accent: '#BF360C',
    // Ordered by use (Javier, 2026-09-19: «lo más importante es catálogo»): the catalog, then what is looked at every
    // day, buying, the recetas cuantificadas (driven by the purchases of controlled medicines, used well beyond the
    // consulting room — moved here from «Clínica»), and the master data last. Seven entries: warehouses and sections
    // share one («Almacenes y secciones», two tabs) instead of splitting the group (his preference, same day).
    items: [
      { link: '/dashboard/catalog', icon: <MenuBook />, label: 'Catálogo' },
      { link: '/dashboard/inventory', icon: <Inventory />, label: 'Inventario' },
      { link: '/dashboard/stock-analysis', icon: <Analytics />, label: 'Análisis de stock', aliases: ['stock', 'análisis de stock'] },
      { link: '/dashboard/orders', icon: <ShoppingCart />, label: 'Pedidos', aliases: ['pedidos', 'compras', 'planificacion', 'proveedor', 'desego', 'melaleuca', 'minimo'] },
      { link: '/dashboard/controlled-prescriptions', icon: <LocalHospital />, label: 'Recetas cuantificadas' },
      { link: '/dashboard/suppliers', icon: <LocalShipping />, label: 'Proveedores', aliases: ['proveedor', 'supplier', 'ficha de proveedor', 'proveedores qvet', 'vincular', 'vinculo', 'promocion', 'promociones', 'bonificacion', 'costo real', 'pronto pago', 'telvet', 'difarvet', 'lista de precios'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/warehouses', icon: <Warehouse />, label: 'Almacenes y secciones', alsoActiveOn: ['/dashboard/sections'], aliases: ['almacenes', 'almacen', 'bodega', 'warehouse', 'corte de existencias'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/sections', label: 'Secciones y familias', hiddenFromSidebar: true, aliases: ['seccion', 'section', 'familia', 'subfamilia', 'caducidad', 'orden de conteo', 'arbol del catalogo'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/inventory-snapshots', label: 'QVET — snapshots inventario', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/movements', label: 'QVET — movimientos', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/transfers', label: 'QVET — traspasos', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/purchases', label: 'QVET — compras', hiddenFromSidebar: true, aliases: ['compras qvet'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/purchase-orders', label: 'QVET — órdenes de compra', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/controlled-prescriptions/recetas-sitpv', label: 'Recetas SITPV (SENASICA)', hiddenFromSidebar: true, aliases: ['sitpv', 'senasica', 'recetas cuantificadas portal', 'receta senasica'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
    ],
  },
  {
    title: '🩺 Clínica',
    accent: '#C2185B',
    items: [
      { link: '/dashboard/studies', icon: <Science />, label: 'Estudios externos', aliases: ['lab'] },
      { link: '/dashboard/studies/history', label: 'Estudios — historial', hiddenFromSidebar: true },
      { link: '/dashboard/studies/catalog', label: 'Estudios — catálogo', hiddenFromSidebar: true },
      { link: '/dashboard/studies/suppliers', label: 'Estudios — proveedores', hiddenFromSidebar: true },
      { link: '/dashboard/ai/medication-sheets', icon: <MedicalInformation />, label: 'Fichas de medicamentos', aliases: ['medicacion', 'meds', 'ai', 'ia'] },
      { link: '/dashboard/ai/clinical-summary', icon: <AutoAwesome />, label: 'Resumen clínico', aliases: ['ai', 'ia', 'resumen clinico'] },
      { link: '/dashboard/ai/communication-helper', icon: <AutoAwesome />, label: 'Asistente de respuestas', aliases: ['whatsapp', 'comunicacion', 'chat', 'manual', 'ai', 'ia'] },
      { link: '/dashboard/google-calendar', icon: <CalendarMonth />, label: 'Google Calendar', aliases: ['agenda', 'citas', 'guia agenda', 'diagnostico agenda', 'scheduling'] },
      { link: '/dashboard/google-calendar/guide', label: 'Google Calendar — guía', hiddenFromSidebar: true, aliases: ['guia agenda', 'como agendar'] },
      { link: '/dashboard/google-calendar/diagnose', label: 'Google Calendar — diagnóstico', hiddenFromSidebar: true, aliases: ['diagnostico citas', 'auditoria agenda'] },
      { link: '/dashboard/contacts/google', icon: <Contacts />, label: 'Contactos WhatsApp', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/clients', label: 'QVET — clientes', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/pets', label: 'QVET — mascotas', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/visits', label: 'QVET — visitas', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
    ],
  },
  {
    title: '💸 Finanzas',
    accent: '#1B5E20',
    items: [
      { link: '/dashboard/cash-reconciliation', icon: <LocalAtm />, label: 'Reconciliación de caja', aliases: ['caja', 'corte'] },
      { link: '/dashboard/accounting', icon: <AccountBalance />, label: 'Contabilidad', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/supplier-payments', icon: <ShoppingCart />, label: 'Pagos a proveedores', aliases: ['pagos proveedores', 'cuentas por pagar', 'próximos pagos', 'vencimientos'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/billing', icon: <MonetizationOn />, label: 'Facturación', aliases: ['cfdi', 'factura'] },
      { link: '/dashboard/analysis', icon: <Analytics />, label: 'Análisis de ventas', authorizedRoles: [WebAppRole.admin], aliases: ['análisis de ventas'] },
      { link: '/dashboard/billing/clients', label: 'Facturación — clientes', hiddenFromSidebar: true, aliases: ['clientes factura'] },
      { link: '/dashboard/billing/invoices/new', label: 'Facturación — nueva factura', hiddenFromSidebar: true, aliases: ['nueva factura', 'crear factura'] },
      { link: '/dashboard/billing/global', label: 'Facturación — facturas globales', hiddenFromSidebar: true, aliases: ['global', 'cfdi global'] },
      { link: '/dashboard/billing/credit-notes', label: 'Facturación — notas de crédito', hiddenFromSidebar: true, aliases: ['notas de crédito', 'nota de credito', 'egresos'] },
      { link: '/dashboard/billing/guia', label: 'Facturación — guía', hiddenFromSidebar: true },
      { link: '/dashboard/qvet/sync/sales', label: 'QVET — ventas', hiddenFromSidebar: true, aliases: ['ventas qvet'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/cash-closings', label: 'QVET — cortes de caja', hiddenFromSidebar: true, aliases: ['cortes caja'], authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/cash-flows', label: 'QVET — flujos de caja', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/collections', label: 'QVET — cobros', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/purchase-invoices', label: 'QVET — facturas de compra', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/payables', label: 'QVET — cuentas por pagar', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
    ],
  },
  {
    title: '📋 Registros y documentos',
    accent: '#6D4C41',
    items: [
      { link: '/dashboard/tasks', icon: <Task />, label: 'Tareas' },
      { link: '/dashboard/tasks/list', label: 'Tareas — lista', hiddenFromSidebar: true, aliases: ['tareas lista'] },
      { link: '/dashboard/tasks/create', label: 'Tareas — crear', hiddenFromSidebar: true, aliases: ['nueva tarea'] },
      { link: '/dashboard/tasks/my-tasks', label: 'Tareas — mis tareas', hiddenFromSidebar: true, aliases: ['my tasks'] },
      { link: '/dashboard/tasks/stats', label: 'Tareas — estadísticas', hiddenFromSidebar: true, aliases: ['stats tareas'] },
      { link: '/dashboard/tasks/report', label: 'Tareas — reporte', hiddenFromSidebar: true },
      { link: '/dashboard/work-logs', icon: <EditNote />, label: 'Bitácoras de tareas' },
      { link: '/dashboard/work-logs/recent', label: 'Bitácoras — recientes', hiddenFromSidebar: true },
      { link: '/dashboard/work-logs/my-logs', label: 'Bitácoras — mías', hiddenFromSidebar: true },
      { link: '/dashboard/work-logs/all-logs', label: 'Bitácoras — todas', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/cleanups/summary', icon: <CleaningServices />, label: 'Limpieza' },
      { link: '/dashboard/documentation', icon: <Article />, label: 'Documentación', aliases: ['docs', 'guia', 'manual', 'memo'] },
      { link: '/dashboard/documentation/docs', label: 'Documentación — documentos', hiddenFromSidebar: true, aliases: ['docs', 'guias'] },
      { link: '/dashboard/documentation/memos', label: 'Documentación — memorandos', hiddenFromSidebar: true, aliases: ['memos'] },
      { link: '/dashboard/documentation/compliance', icon: <Assignment />, label: 'Documentación — control de lectura', authorizedRoles: [WebAppRole.admin, WebAppRole.manager], aliases: ['cumplimiento', 'lectura', 'acuse'] },
    ],
  },
  {
    title: '⚙️ Ajustes y sistema',
    accent: '#546E7A',
    items: [
      // Sólo el administrador: RFC, régimen fiscal y la clave de Facturama (GH125)
      { link: '/dashboard/settings/company', icon: <Business />, label: 'Empresa', authorizedRoles: [WebAppRole.admin] },
      { link: '/dashboard/branches', icon: <Business />, label: 'Sucursales', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/notifications', icon: <NotificationsNone />, label: 'Notificaciones', aliases: ['notifications', 'notificaciones'] },
      { link: '/dashboard/authLogs', icon: <AccessTime />, label: 'Log de acceso', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      // El panel de sincronización: vigilar los espejos de sólo lectura y relanzar barridos. Es
      // una herramienta de sistema, no un sitio donde se decida nada del hospital.
      { link: '/dashboard/qvet/sync', icon: <Sync />, label: 'Sync QVET', authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      /*
       * 🆕 Las 20 listas de parámetros de QVET, con alta, cambio y baja (GH527 · SP10).
       *
       * Va aquí y no con inventario, aunque las marcas y las unidades se usen allí: **no son
       * una operación, son el vocabulario** que el resto de la app usa para nombrar cosas, y
       * cambiarlo es configurar. Su vecino natural es el panel de sincronización, que es de
       * donde se llega.
       *
       * ⚠️ Y había un motivo más, medido: el grupo de inventario ya tenía **siete** entradas
       * visibles y la regla de este fichero dice que a la octava se parte el grupo, no se añade.
       */
      { link: '/dashboard/qvet-catalogs', icon: <MenuBook />, label: 'Catálogos de parámetros', aliases: ['marcas', 'iva', 'unidades', 'vias', 'principios activos', 'familias', 'subfamilias', 'laboratorios', 'parametros', 'tarifas'] },
      { link: '/dashboard/qvet/sync/job-history', label: 'QVET — historial de jobs', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
      { link: '/dashboard/qvet/sync/scheduled-logs', label: 'QVET — logs programados', hiddenFromSidebar: true, authorizedRoles: [WebAppRole.admin, WebAppRole.manager] },
    ],
  },
];
/**
 * Flattens all menu items respecting role visibility, returning a list usable
 * by the Cmd+K palette. Each command remembers its section title for grouping.
 * Sub-routes flagged `hiddenFromSidebar` are INCLUDED here on purpose — the
 * sidebar filters them out separately.
 */
export function flatNavigationCommands(role: WebAppRole | undefined): Array<MenuItem & { section: string }> {
  const out: Array<MenuItem & { section: string }> = [];
  for (const section of menuSections) {
    for (const item of section.items) {
      if (item.authorizedRoles && (!role || !item.authorizedRoles.includes(role))) continue;
      out.push({ ...item, section: section.title });
    }
  }
  return out;
}

/**
 * Cuál de las entradas visibles corresponde a la ruta actual: **la coincidencia más larga**.
 *
 * 🚨 Existe porque cada entrada lo decidía por su cuenta con `startsWith`, y así **se encendían
 * dos a la vez** en cuanto una ruta era prefijo de otra —`/dashboard/documentation` y
 * `/dashboard/documentation/compliance`, o «Sync QVET» y cualquiera colgada de ella—.
 *
 * La coincidencia es **por segmentos**: `/dashboard/sections` no es prefijo de
 * `/dashboard/sections-de-otra-cosa`. Y sigue siendo por prefijo a propósito, para que una
 * entrada padre se quede encendida en sus subrutas —«Tareas» en `/dashboard/tasks/list`— salvo
 * que la subruta tenga entrada propia, que entonces gana ella por ser más larga.
 */
export function activeSidebarLink(pathname: string, role?: WebAppRole): string | undefined {
  const matches = (link: string) => pathname === link || pathname.startsWith(link + '/');
  const items = visibleSidebarSections(role).flatMap((s) => s.items);
  const direct = items.map((i) => i.link).filter(matches).sort((a, b) => b.length - a.length)[0];
  return direct ?? items.find((i) => i.alsoActiveOn?.some(matches))?.link;
}

/**
 * Sidebar consumer: drops sub-route entries that should only appear in the
 * Cmd+K palette, plus anything this role cannot open.
 *
 * 🚨 **Y descarta los grupos que se quedan sin nada**, que antes no hacía. El filtro por rol
 * vivía en el componente, después de pintar el encabezado, así que un grupo cuyas entradas
 * fueran todas de gerencia le enseñaba **el título y nada debajo** a todos los demás. Pasaba en
 * dos: «Medical» —su única entrada está oculta— para todo el mundo, y «Análisis» para quien no
 * fuera gerencia.
 */
export function visibleSidebarSections(role?: WebAppRole): MenuSection[] {
  return menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          !item.hiddenFromSidebar &&
          (!item.authorizedRoles || (!!role && item.authorizedRoles.includes(role)))
      ),
    }))
    .filter((section) => section.items.length > 0);
}
